import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CanonicalWorkflowDocument,
  FormDocument,
  TenantConfigPackManifest,
} from '../models/canonical.types';

export type LoadedConfigPack = {
  manifest: TenantConfigPackManifest;
  workflows: Map<string, CanonicalWorkflowDocument>;
  formsById: Map<string, FormDocument>;
  formsByCode: Map<string, FormDocument>;
  fields: unknown;
  basePath: string;
};

type ManifestFormEntry = TenantConfigPackManifest['forms'][number];

let manifestWarningsLogged = false;

function logManifestWarnings(manifest: TenantConfigPackManifest): void {
  if (manifestWarningsLogged || !manifest.warnings?.length) return;
  manifestWarningsLogged = true;
  if (typeof ngDevMode !== 'undefined' && !ngDevMode) return;

  const lines = manifest.warnings.map((warning) => {
    if (warning && typeof warning === 'object' && 'message' in warning) {
      const code =
        'code' in warning && warning.code ? `[${String(warning.code)}] ` : '';
      return `${code}${String(warning.message)}`;
    }
    return String(warning);
  });
  const preview = lines.slice(0, 5).join('\n  • ');
  const more =
    lines.length > 5 ? `\n  … and ${lines.length - 5} more (see manifest.json warnings)` : '';
  console.warn(
    `[ConfigPack] ${lines.length} manifest warning(s):\n  • ${preview}${more}`,
  );
}

/**
 * Loads tenant workflow-config packs.
 * Workflows + fields load eagerly; forms load on demand so CREATE / search
 * JSON files are only fetched when the matching applicant type is selected.
 */
@Injectable({ providedIn: 'root' })
export class ConfigPackLoader {
  private readonly http = inject(HttpClient);
  private pack: LoadedConfigPack | null = null;
  private loading: Promise<LoadedConfigPack> | null = null;
  private readonly formLoads = new Map<string, Promise<FormDocument | undefined>>();

  async load(basePath = '/assets/workflow-config'): Promise<LoadedConfigPack> {
    if (this.pack?.basePath === basePath) return this.pack;
    if (this.loading) return this.loading;

    this.loading = this.loadInternal(basePath);
    try {
      this.pack = await this.loading;
      return this.pack;
    } finally {
      this.loading = null;
    }
  }

  getPack(): LoadedConfigPack | null {
    return this.pack;
  }

  async getWorkflow(workflowKey: string): Promise<CanonicalWorkflowDocument> {
    const pack = await this.load();
    const key = workflowKey.toUpperCase();
    const doc =
      pack.workflows.get(key) ??
      [...pack.workflows.values()].find((w) => w.workflowKey.toUpperCase() === key);
    if (!doc) throw new Error(`Workflow "${workflowKey}" not found in config pack`);
    return doc;
  }

  /**
   * Resolve a workflow key from manifest.json.
   * Prefers an explicit key (e.g. OPPORTUNITY); otherwise uses the first manifest entry.
   */
  async resolveWorkflowKey(preferredKey = 'OPPORTUNITY'): Promise<string> {
    const pack = await this.load();
    const preferred = preferredKey.trim().toUpperCase();
    const match = pack.manifest.workflows.find(
      (entry) => entry.workflowKey.toUpperCase() === preferred,
    );
    if (match) return match.workflowKey;

    const first = pack.manifest.workflows[0];
    if (!first) {
      throw new Error('No workflows declared in workflow-config manifest.json');
    }
    return first.workflowKey;
  }

  /** Manifest entry for a workflow key (path, version, checksum). */
  async getManifestWorkflowEntry(
    preferredKey = 'OPPORTUNITY',
  ): Promise<TenantConfigPackManifest['workflows'][number]> {
    const pack = await this.load();
    const key = await this.resolveWorkflowKey(preferredKey);
    const entry = pack.manifest.workflows.find(
      (workflow) => workflow.workflowKey.toUpperCase() === key.toUpperCase(),
    );
    if (!entry) {
      throw new Error(`Workflow "${key}" not found in workflow-config manifest.json`);
    }
    return entry;
  }

  async getFormById(formId: string): Promise<FormDocument | undefined> {
    const pack = await this.load();
    const cached = pack.formsById.get(formId);
    if (cached) return cached;

    const entry = pack.manifest.forms.find((f) => f.formId === formId);
    if (!entry) return undefined;
    return this.loadFormEntry(pack, entry);
  }

  async getFormByCode(formCode: string): Promise<FormDocument | undefined> {
    const pack = await this.load();
    const cached = pack.formsByCode.get(formCode);
    if (cached) return cached;

    const entry = pack.manifest.forms.find((f) => f.formCode === formCode);
    if (!entry) return undefined;
    return this.loadFormEntry(pack, entry);
  }

  private async loadInternal(basePath: string): Promise<LoadedConfigPack> {
    const root = basePath.replace(/\/$/, '');
    const manifest = await firstValueFrom(
      this.http.get<TenantConfigPackManifest>(`${root}/manifest.json`),
    );
    logManifestWarnings(manifest);

    const workflows = new Map<string, CanonicalWorkflowDocument>();
    for (const entry of manifest.workflows) {
      const doc = await firstValueFrom(
        this.http.get<CanonicalWorkflowDocument>(`${root}/${entry.path}`),
      );
      workflows.set(entry.workflowKey.toUpperCase(), doc);
    }

    // Forms are loaded lazily via getFormById / getFormByCode.
    const formsById = new Map<string, FormDocument>();
    const formsByCode = new Map<string, FormDocument>();

    let fields: unknown = [];
    if (manifest.fields?.path) {
      try {
        fields = await firstValueFrom(this.http.get(`${root}/${manifest.fields.path}`));
      } catch {
        fields = [];
      }
    }

    return { manifest, workflows, formsById, formsByCode, fields, basePath: root };
  }

  private loadFormEntry(
    pack: LoadedConfigPack,
    entry: ManifestFormEntry,
  ): Promise<FormDocument | undefined> {
    const inflightKey = `${pack.basePath}:${entry.formId}`;
    const inflight = this.formLoads.get(inflightKey);
    if (inflight) return inflight;

    const promise = (async () => {
      try {
        const form = await firstValueFrom(
          this.http.get<FormDocument>(`${pack.basePath}/${entry.path}`),
        );
        pack.formsById.set(entry.formId, form);
        pack.formsByCode.set(entry.formCode, form);
        return form;
      } catch {
        return undefined;
      } finally {
        this.formLoads.delete(inflightKey);
      }
    })();

    this.formLoads.set(inflightKey, promise);
    return promise;
  }
}
